/**
 * SitStay brand layer for Horizon.
 *
 * - Purchase options (Subscribe & Save / One-time) wired into Horizon's product form via the
 *   `selling_plan` input.
 * - Free-shipping progress on the product page, kept current with the cart and selected price.
 * - One-tap add for "Pairs well with" and the featured bundle, announced to Horizon's cart
 *   drawer and cart icon through the standard cart events.
 * - A small chip "pop" on the cart icon when an add succeeds (skipped for reduced motion).
 * - Ingredient tile flip on tap.
 * - Re-renders SitStay product blocks when the variant changes.
 *
 * No third-party code. Everything degrades to plain links and forms without JavaScript.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const config = /** @type {{ moneyFormat?: string, currency?: string }} */ (window.SitStay || {});

/** @param {number} cents */
function money(cents) {
  try {
    return formatMoney(cents, config.moneyFormat || '${{amount}}', config.currency || 'USD');
  } catch (error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Latest known cart total in cents, or null until the cart changes on this page. */
let cartTotal = /** @type {number | null} */ (null);

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  const cart = await response.json();
  cartTotal = Number(cart.total_price) || 0;
  return cart;
}

/* ---------------------------------------------------------------------------
 * Free-shipping progress (product page)
 * ------------------------------------------------------------------------- */
function updateFreeShipping(root = document) {
  root.querySelectorAll('[data-ss-free-ship][data-context="product"]').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    const threshold = Number(element.dataset.threshold) || 4500;
    const total = cartTotal !== null ? cartTotal : Number(element.dataset.cartTotal) || 0;
    const current = Number(element.dataset.currentPrice) || 0;
    const sum = total + current;
    const subscribing = element.dataset.subscribe === 'true';
    const remaining = subscribing ? 0 : Math.max(0, threshold - sum);
    const progress = subscribing ? 100 : Math.min(100, Math.round((sum * 100) / threshold));

    element.style.setProperty('--ss-ship-progress', `${progress}%`);
    element.classList.toggle('ss-ship--unlocked', remaining === 0);
    element.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(progress));

    const text = element.querySelector('[data-ss-free-ship-text]');
    let message = `You're ${money(remaining)} away from free shipping`;
    if (subscribing) message = 'Subscriptions always ship free';
    else if (remaining === 0) message = "You've unlocked free shipping";
    if (text && text.textContent?.trim() !== message) text.textContent = message;
  });
}

/**
 * Sets the price the product-page free-shipping line should add to the cart total, and whether the
 * shopper is subscribing (subscriptions ship free).
 */
function setCurrentPrice(scope, cents, subscribing = false) {
  scope.querySelectorAll('[data-ss-free-ship][data-context="product"]').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    element.dataset.currentPrice = String(cents);
    element.dataset.subscribe = String(subscribing);
  });
  updateFreeShipping(scope);
}

/* ---------------------------------------------------------------------------
 * Purchase options
 * ------------------------------------------------------------------------- */
/** Remembers the shopper's choice per block so a variant re-render keeps it. */
const purchaseChoices = new Map();

/** The quantity input of the product form this block buys through. */
function quantityInput(wrapper) {
  const form = document.getElementById(wrapper.dataset.formId || '');
  return /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]') || null);
}

function syncPurchaseOptions(wrapper) {
  const blockId = wrapper.dataset.blockId;
  const checked = /** @type {HTMLInputElement | null} */ (
    wrapper.querySelector('input[name^="ss-purchase-"]:checked')
  );
  const select = /** @type {HTMLSelectElement | null} */ (wrapper.querySelector('[data-ss-po-plan]'));
  const bagsInput = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-ss-po-bags]:checked'));
  const isSubscribe = checked?.value === 'subscribe';
  const bags = Math.max(1, Number(bagsInput?.value) || 1);
  const oneTime = Number(wrapper.dataset.oneTimePrice) || 0;

  if (select) select.disabled = !isSubscribe;

  const quantity = quantityInput(wrapper);
  if (quantity && quantity.value !== String(bags)) {
    quantity.value = String(bags);
    quantity.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const planPrice = Number(select?.selectedOptions[0]?.dataset.price) || oneTime;
  const setText = (selector, text) => {
    const element = wrapper.querySelector(selector);
    if (element && text) element.textContent = text;
  };
  setText('[data-ss-po-sub-price]', money(planPrice * bags));
  setText('[data-ss-po-compare]', money(oneTime * bags));
  setText('[data-ss-po-save]', `Save ${money((oneTime - planPrice) * bags)}`);
  setText('[data-ss-po-once-price]', money(oneTime * bags));

  const unit = isSubscribe ? planPrice : oneTime;
  purchaseChoices.set(blockId, { type: checked?.value, plan: select?.value, bags: String(bags) });

  const section = wrapper.closest('.shopify-section') || document;
  setCurrentPrice(section, unit * bags, isSubscribe);
}

function initPurchaseOptions(root = document) {
  root.querySelectorAll('[data-ss-purchase-options]').forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;

    const saved = purchaseChoices.get(wrapper.dataset.blockId);
    if (saved) {
      const radio = /** @type {HTMLInputElement | null} */ (
        wrapper.querySelector(`input[type="radio"][value="${saved.type}"]`)
      );
      if (radio) radio.checked = true;
      const select = /** @type {HTMLSelectElement | null} */ (wrapper.querySelector('[data-ss-po-plan]'));
      if (select && saved.plan && select.querySelector(`option[value="${saved.plan}"]`)) select.value = saved.plan;
      const bags = /** @type {HTMLInputElement | null} */ (
        wrapper.querySelector(`[data-ss-po-bags][value="${saved.bags}"]`)
      );
      if (bags) bags.checked = true;
    }

    if (wrapper.dataset.ssBound !== 'true') {
      wrapper.dataset.ssBound = 'true';
      wrapper.addEventListener('change', () => syncPurchaseOptions(wrapper));
    }
    syncPurchaseOptions(wrapper);
  });
}

/* ---------------------------------------------------------------------------
 * Variant changes: refresh SitStay blocks from the new section HTML
 * ------------------------------------------------------------------------- */
document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target;
  if (!(target instanceof Element) || target.closest('product-card')) return;
  const section = target.closest('.shopify-section');
  if (!section) return;

  /** @type {any} */ (event).promise
    ?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;
      section.querySelectorAll('[data-ss-refresh]').forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        const fresh = html.querySelector(`[data-ss-refresh="${element.dataset.ssRefresh}"]`);
        if (fresh) element.replaceWith(document.importNode(fresh, true));
      });
      initPurchaseOptions(section);
      updateFreeShipping(section);
    })
    .catch(() => {});
});

/* ---------------------------------------------------------------------------
 * Chip pop on the cart icon after a successful add
 * ------------------------------------------------------------------------- */
function popChip() {
  if (reducedMotion()) return;
  const icons = Array.from(document.querySelectorAll('.header-actions__cart-icon'));
  const icon = icons.find((element) => element.getBoundingClientRect().width > 0);
  if (!icon) return;
  const rect = icon.getBoundingClientRect();
  const chip = document.createElement('span');
  chip.className = 'ss-chip-pop';
  chip.setAttribute('aria-hidden', 'true');
  chip.style.left = `${rect.left + rect.width / 2}px`;
  chip.style.top = `${rect.top + rect.height / 2}px`;
  document.body.appendChild(chip);
  requestAnimationFrame(() => chip.classList.add('is-popping'));
  chip.addEventListener('animationend', () => chip.remove(), { once: true });
  window.setTimeout(() => chip.remove(), 1500);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const cartEvent = /** @type {any} */ (event);
  cartEvent.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      if (cartEvent.action === 'add') popChip();
      return fetchCart().then(() => updateFreeShipping());
    })
    .catch(() => {});
});

/* ---------------------------------------------------------------------------
 * One-tap add (pairs well with, featured bundle)
 * ------------------------------------------------------------------------- */
async function quickAdd(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.classList.contains('is-loading')) return;

  const label = button.querySelector('[data-ss-add-label]') || button;
  const originalText = label.textContent;
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', '1');
  if (button.dataset.sellingPlan) body.append('selling_plan', button.dataset.sellingPlan);
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body,
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'sitstay-quick-add',
        itemCount: 1,
        sections: data.sections,
        didError: false,
      },
    });

    button.classList.add('is-added');
    label.textContent = 'Added';
    window.setTimeout(() => {
      button.classList.remove('is-added');
      label.textContent = originalText;
    }, 2000);
  } catch (error) {
    const cart = await fetchCart().catch(() => null);
    deferred.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail: { didError: true, source: 'sitstay-quick-add', itemCount: 0 },
    });
    label.textContent = 'Try again';
    window.setTimeout(() => {
      label.textContent = originalText;
    }, 2500);
  } finally {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const addButton = target.closest('[data-ss-quick-add]');
  if (addButton instanceof HTMLButtonElement) {
    event.preventDefault();
    quickAdd(addButton);
    return;
  }

  const tile = target.closest('[data-ss-tile]');
  if (tile instanceof HTMLButtonElement) {
    tile.setAttribute('aria-pressed', tile.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
  }
});

/* ---------------------------------------------------------------------------
 * Boot
 * ------------------------------------------------------------------------- */
/** Autoplaying section videos stop and gain controls for shoppers who ask for reduced motion. */
function respectReducedMotionVideos(root = document) {
  if (!reducedMotion()) return;
  root.querySelectorAll('.ss-mwc__media video').forEach((video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    video.autoplay = false;
    video.pause();
    video.controls = true;
  });
}

/**
 * Review-request emails link to a product with #write-review. Show the reviews section even when it
 * has no reviews yet, then open Judge.me's write-a-review form once the widget has loaded.
 */
function openWriteReview() {
  if (location.hash !== '#write-review') return;
  document.documentElement.classList.add('ss-write-review');
  let tries = 0;
  const timer = setInterval(() => {
    tries += 1;
    const widget = document.querySelector('.jdgm-review-widget');
    const button =
      document.querySelector('.jdgm-write-rev-link') ||
      (widget &&
        [...widget.querySelectorAll('button, a')].find((el) => /^\s*write a review\s*$/i.test(el.textContent)));
    if (button) {
      clearInterval(timer);
      const section = document.getElementById('reviews') || button;
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      button.click();
    } else if (tries > 40) {
      clearInterval(timer);
    }
  }, 250);
}

/**
 * Product reviews stay hidden until Judge.me reports at least one review (read from its star
 * badge), or the visitor came to write one. Checks for up to 15 seconds, then stops.
 */
function revealReviews() {
  const gates = document.querySelectorAll('[data-ss-reviews-gate]');
  if (!gates.length) return;
  const show = () => gates.forEach((gate) => gate.classList.add('is-visible'));
  if (location.hash === '#write-review') return show();
  let tries = 0;
  const timer = setInterval(() => {
    tries += 1;
    const badge = document.querySelector('.jdgm-prev-badge[data-number-of-reviews]');
    if (badge) {
      clearInterval(timer);
      if (Number(badge.getAttribute('data-number-of-reviews')) > 0) show();
    } else if (tries > 30) {
      clearInterval(timer);
    }
  }, 500);
}

/** Pauses the decorative wave while it is off screen. */
function watchWaves() {
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.classList.toggle('is-offscreen', !entry.isIntersecting));
  });
  document.querySelectorAll('.ss-wave').forEach((wave) => observer.observe(wave));
}

/**
 * Loads every image in a gallery once the gallery is near the screen. Some browsers (Safari in
 * particular) never fetch lazy images that sit off to the side inside a scroller, which leaves a
 * blank slide when it is swiped or clicked into view.
 */
function loadGalleryImages(root = document) {
  const galleries = root.querySelectorAll('slideshow-component');
  const load = (gallery) => {
    gallery.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };
  if (!('IntersectionObserver' in window)) {
    galleries.forEach(load);
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        load(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '400px' }
  );
  galleries.forEach((gallery) => observer.observe(gallery));
}

function boot() {
  // ?ss-safe=js (see sitstay-head.liquid) runs the page without the brand script.
  const safe = (document.documentElement.getAttribute('data-ss-safe') || '').split(' ');
  if (safe.includes('js') || safe.includes('all')) return;
  openWriteReview();
  revealReviews();
  watchWaves();
  if (document.querySelector('[data-ss-purchase-options]')) document.body.classList.add('ss-has-bag-picker');
  initPurchaseOptions();
  updateFreeShipping();
  respectReducedMotionVideos();
  loadGalleryImages();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof Element ? event.target : document;
  initPurchaseOptions(target);
  updateFreeShipping(target);
  loadGalleryImages(target);
});
document.addEventListener('shopify:block:select', () => boot());
